import { Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import Home from './pages/Home'
import LanguageSwitcher from './components/LanguageSwitcher'
import logo from './assets/virtum-logo-192.webp'

export default function App() {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)

  // Zatvorenie menu pri kliknutí mimo alebo stlačení Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    if (mobileOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  const navLinks = [
    { href: '#top', label: t('nav.home'), icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    )},
    { href: '#services', label: t('nav.services'), icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 7.125a2.18 2.18 0 0 1 .75-1.661V9.706c0-1.081-.768-2.015-1.837-2.175A48.111 48.111 0 0 1 3.63 7.07m15.787 7.175a48.116 48.116 0 0 1-3.572 0M6.375 7.125a48.116 48.116 0 0 1 3.572 0m9.375 2.625a48.116 48.116 0 0 1 3.572 0M3.375 9.75c-.088.967-.089 1.947-.003 2.916.085.985.239 1.952.458 2.89M20.625 9.75c.088.967.089 1.947.003 2.916-.085.985-.239 1.952-.458 2.89M9.75 9.75l1.902 3.521 3.572.458-2.585 2.52.61 3.552-3.208-1.686-3.208 1.686.61-3.552-2.585-2.52 3.572-.458L9.75 9.75Z" />
      </svg>
    )},
    { href: '#projects', label: t('nav.projects'), icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
      </svg>
    )},
    { href: '#about', label: t('nav.about'), icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.592-2.241m2.588 3.816v-.106c0-1.113.285-2.16.786-3.07m-.786 3.07 2.17 1.027a3 3 0 0 0 2.296.044l2.17-1.027m-4.636-1.972a6.375 6.375 0 0 1 11.592 2.241l.001.109M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>
    )},
    { href: '#contact', label: t('nav.contact'), icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.2 2.2 0 0 1-1.708 2.136l-6.75 1.5a2.25 2.25 0 0 1-.984 0l-6.75-1.5A2.2 2.2 0 0 1 1.5 6.993V6.75" />
      </svg>
    )},
  ]

  const handleNavClick = () => {
    setMobileOpen(false)
  }

  return (
    <div className="min-h-[100dvh] flex flex-col overflow-x-clip bg-[linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#1e3a8a_100%)]">
      <header className="sticky top-0 z-50 flex items-center justify-between py-3 px-6 border-b border-blue-200/60 bg-[linear-gradient(180deg,rgba(219,234,254,0.98)_0%,rgba(191,219,254,0.96)_100%)] backdrop-blur-md shadow-[0_12px_30px_rgba(59,130,246,0.15)]">
        <div className="flex items-center gap-8">
          <a href="#top" className="inline-flex items-center px-2 py-1">
            <img src={logo} width={192} height={128} alt="Virtum" className="h-14 md:h-16 w-auto drop-shadow-[0_4px_14px_rgba(56,189,248,0.2)]" />
          </a>
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(l => (
              <a
                key={l.href}
                href={l.href}
                className="px-4 py-2 rounded-lg text-sm text-blue-900 hover:text-blue-950 hover:bg-white/70 transition-all"
              >
                {l.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            aria-label={mobileOpen ? t('a11y.menu_close') : t('a11y.menu_open')}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(v => !v)}
            className="md:hidden relative inline-flex items-center justify-center w-10 h-10 rounded-xl border border-blue-300/80 text-blue-900 hover:text-blue-950 hover:bg-white/70 hover:border-blue-400 transition-all duration-200"
          >
            <div className="relative w-5 h-5">
              {/* Horná čiara */}
              <span className={`absolute left-0 top-1 w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
              {/* Stredná čiara */}
              <span className={`absolute left-0 top-2.5 w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${mobileOpen ? 'opacity-0 scale-x-0' : ''}`} />
              {/* Spodná čiara */}
              <span className={`absolute left-0 top-4 w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
            </div>
          </button>
        </div>
      </header>

      {/* Mobilné menu - prepojené s hlavičkou */}
      {mobileOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] z-40 md:hidden animate-fadeIn"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* Menu panel - priamo pod hlavičkou s rovnakým štýlom */}
          <div className="fixed top-[65px] left-0 right-0 z-50 md:hidden">
            <div className="mx-0 bg-[linear-gradient(180deg,rgba(219,234,254,0.98)_0%,rgba(229,242,255,0.98)_100%)] border-b border-blue-200/60 shadow-lg">
              <nav className="flex flex-col py-2">
                {navLinks.map((l, index) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={handleNavClick}
                    className="group flex items-center gap-3 px-6 py-3 text-blue-900 hover:text-blue-950 hover:bg-white/50 transition-all duration-200 animate-slideIn border-l-4 border-transparent hover:border-blue-500"
                    style={{ animationDelay: `${index * 40}ms` }}
                  >
                    <span className="text-blue-600 group-hover:text-blue-700 transition-colors">
                      {l.icon}
                    </span>
                    <span className="text-sm font-medium">{l.label}</span>
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </>
      )}

      <main className="flex-1 bg-transparent">
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </main>

      <footer className="py-4 px-6 border-t border-blue-200/60 bg-[linear-gradient(180deg,rgba(219,234,254,0.98)_0%,rgba(191,219,254,0.96)_100%)] backdrop-blur-sm shadow-[0_-10px_24px_rgba(59,130,246,0.10)]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <img src={logo} width={192} height={128} alt="Virtum" className="h-8 w-auto drop-shadow-[0_4px_14px_rgba(56,189,248,0.2)]" />
            <span className="text-xs text-blue-900">{t('footer.company')}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-blue-900">
            <a href="mailto:info@virtum.sk" className="hover:text-blue-950 transition-colors">info@virtum.sk</a>
            <span>© {new Date().getFullYear()} Virtum. {t('footer.rights')}</span>
          </div>
        </div>
      </footer>
    </div>
  )
}